import { useState } from 'react';

export function CodeBlock({ label, commands }: { label: string; commands: string[] }) {
    const [copied, setCopied] = useState(false);
    const text = commands.join('\n');

    const handleCopy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="my-2.5 rounded-lg overflow-hidden bg-gray-900 border border-white/10">
            <div className="flex items-center justify-between px-3 py-2 bg-white/[0.02] border-b border-white/5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-200">{label}</span>
                <button
                    onClick={handleCopy}
                    className={`px-2.5 py-0.5 text-[10px] font-semibold rounded-md transition-all ${copied
                        ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                        : 'bg-gray-800 text-gray-200 border border-transparent hover:bg-gray-700 hover:text-white'
                        }`}
                >
                    {copied ? '✓ Copied' : 'Copy'}
                </button>
            </div>
            <div className="p-3 font-mono text-[12px] leading-relaxed text-gray-200 overflow-x-auto">
                {commands.map((cmd, i) => (
                    <div key={i} className="whitespace-pre">
                        <span className="text-orange-400 select-none">$ </span>
                        {cmd}
                    </div>
                ))}
            </div>
        </div>
    );
}

export function StepItem({ num, title, children }: { num: number; title: string; children: React.ReactNode }) {
    return (
        <div className="flex gap-3 relative group">
            {/* Connector line */}
            <div className="absolute left-[13px] top-8 bottom-[-16px] w-[1px] bg-gradient-to-b from-orange-500/30 to-transparent group-last:hidden"></div>

            <div className="relative z-10 w-7 h-7 min-w-[28px] bg-gray-800/80 border-2 border-orange-500/50 rounded-full flex items-center justify-center text-xs font-bold text-orange-400 mt-0.5">
                {num}
            </div>

            <div className="flex-1 pb-2">
                <div className="text-[14px] font-bold text-gray-100 mb-1">{title}</div>
                <div className="text-[13px] text-gray-200 leading-relaxed space-y-1.5">{children}</div>
            </div>
        </div>
    );
}

export function AlertBox({ type, icon, children }: { type: string; icon: React.ReactNode; children: React.ReactNode }) {
    let styleClasses = '';

    switch (type) {
        case 'note':
            styleClasses = 'bg-blue-500/10 border-blue-500/20 text-blue-400';
            break;
        case 'warning':
            styleClasses = 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400';
            break;
        case 'success':
            styleClasses = 'bg-green-500/10 border-green-500/20 text-green-400';
            break;
        case 'error':
            styleClasses = 'bg-red-500/10 border-red-500/20 text-red-500';
            break;
        default:
            styleClasses = 'bg-gray-500/10 border-gray-500/20 text-gray-200';
    }

    return (
        <div className={`my-3 p-3 rounded-lg flex gap-2.5 text-[13px] leading-relaxed border ${styleClasses}`}>
            <span className="text-base shrink-0 mt-[1px]">{icon}</span>
            <div>{children}</div>
        </div>
    );
}

export function Screenshot({ src, alt }: { src: string; alt: string }) {
    return (
        <div className="mt-3 overflow-hidden rounded-xl bg-gray-950/80 border border-white/10 shadow-xl shadow-black/50 max-w-xl transition-all duration-300 hover:shadow-orange-500/20 hover:border-orange-500/40 group relative">
            <div className="h-5 bg-gray-900 border-b border-white/5 flex items-center px-3 gap-1.5 opacity-90">
                <div className="w-2 h-2 rounded-full bg-red-500/80 transition-colors duration-300"></div>
                <div className="w-2 h-2 rounded-full bg-yellow-500/80 transition-colors duration-300"></div>
                <div className="w-2 h-2 rounded-full bg-green-500/80 transition-colors duration-300"></div>
                <div className="ml-1.5 text-[9px] text-gray-200 font-medium tracking-widest truncate">{alt}</div>
            </div>
            <div className="bg-gray-950/50">
                <img src={src} alt={alt} className="w-full h-auto  duration-300" loading="lazy" />
            </div>
            {/* Glossy overlay effect for compact sleek look */}
            <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/[0.02] to-white/0 pointer-events-none"></div>
        </div>
    );
}

export function BulletList({ items }: { items: string[] }) {
    return (
        <div className="space-y-1.5">
            {items.map((item, index) => (
                <div
                    key={index}
                    className="flex items-center gap-2.5 text-[13px] text-gray-200 leading-relaxed group"
                >
                    <span className="text-orange-500/80 font-bold shrink-0 group-hover:text-orange-400 transition-colors">
                        {'>'}
                    </span>
                    <span className="group-hover:text-white transition-colors">{item}</span>
                </div>
            ))}
        </div>
    );
}

export function ScreenshotGrid({ children }: { children: React.ReactNode }) {
    return (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-5xl">
            {children}
        </div>
    );
}
